import { useState, useMemo } from 'react'
import Link from 'next/link'
import { Cell, PieChart, Pie, Legend, Tooltip, ComposedChart, ResponsiveContainer, Bar, Line, CartesianGrid, XAxis, YAxis } from 'recharts'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Layout from 'components/Layout'
import Statistic from '@byma/shared/components/Statistic'
import { money } from '@byma/shared/helpers/formatters'
import SalesChart from 'components/SalesChart'
import DateFilter from '@byma/shared/components/DateFilter'
import ChannelFilter from '@byma/shared/components/ChannelFilter'
import CouponFilter from '@byma/shared/components/CouponFilter'
import EventFilter from '@byma/shared/components/EventFilter'
import TicketTypesOverview from 'components/TicketTypesOverview'
import ChannelsOverview from 'components/ChannelsOverview'
import CategoriesOverview from 'components/CategoriesOverview'
import CouponsOverview from 'components/CouponsOverview'
import PaymentMethodsOverview from 'components/PaymentMethodsOverview'
import Tabs from '@byma/shared/components/Tabs'
import Loader from '@byma/shared/components/Loader'
import Gauge from '@byma/shared/components/Gauge'
import useAuth from '@byma/shared/hooks/useAuth'
import useFilters from '@byma/shared/hooks/useFilters'
import fetcher from '@byma/shared/helpers/fetcher'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { FiShoppingBag, FiDollarSign, FiCalendar } from 'react-icons/fi'


function reportSum(report, key) {
    if (!report) return 0
    return report.reduce((acc, cur) => acc += cur[key], 0)
}

function getDaysArray(s,e) {for(var a=[],d=new Date(s);d<=e;d.setDate(d.getDate()+1)){ a.push(new Date(d));}return a;}

export default function Report() {
    const session = useAuth()
    const { token, user: currentUser } = session
    const { query, ...router } = useRouter()
    const { parsedFilters, queryString, noSearchQueryString, rawFilters } = useFilters(query)
    const { data: paymentReport } = useSWR(token ? [`/api/payments/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: ticketReport } = useSWR(token ? [`/api/tickets/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: ticketChartReport } = useSWR(token ? [`/api/tickets/chart?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: channelReport } = useSWR(token ? [`/api/channels/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })
    const { data: couponReport } = useSWR(token ? [`/api/coupons/report?${queryString}`, null, token] : null, fetcher, { revalidateOnFocus: false })

    const [dateFilterOpen, setDateFilterOpen] = useState(false);
    const [showIngressoMedio, setShowIngressoMedio] = useState(true);

    const parsedChannelReport = useMemo(() => {
        if (channelReport?.result) {
            const joined = channelReport?.result.reduce((acc, cur) => {
                let channel = acc[cur?._id?.created_by?._id] || {
                    count: 0,
                    tickets: 0,
                    total: 0,
                }
                acc[cur?._id?.created_by?._id] = {
                    _id: {
                        created_by: cur?._id?.created_by,
                    },
                    total: channel.total + cur.total,
                    tickets: channel.tickets + cur.tickets,
                    count: channel.count + cur.count,
                }
                return acc
            }, {})
            return { result: Object.values(joined) }
        }
        return null
    }, [channelReport])

    const tabs = [
        { title: 'Lotes', content: <TicketTypesOverview {...{ticketReport}} /> },
        { title: 'Categorias', content: <CategoriesOverview {...{ticketReport}} /> },
        { title: 'Canais', content: <ChannelsOverview {...{channelReport: parsedChannelReport}} /> },
        { title: 'Cupons', content: <CouponsOverview {...{couponReport}} /> },
        { title: 'Pagamento', content: <PaymentMethodsOverview {...{paymentReport}} /> },
    ] 

    const ticketChartDateObj = ticketChartReport ? ticketChartReport.result.reduce((acc, cur) => {
        if (!acc[cur._id.dateBought]) {
            acc[cur._id.dateBought] = {
                count: 0,
            }
        } 
        acc[cur._id.dateBought].count += cur.count
        return acc
    }, {}) : {}
    const reportDateObj = paymentReport ? paymentReport.result.reduce((acc, cur) => {
        if (!acc[cur._id.created_at]) {
            acc[cur._id.created_at] = {
                order_amount_cents: 0,
                count: 0,
            }
        } 
        acc[cur._id.created_at].count += cur.count
        acc[cur._id.created_at].order_amount_cents += cur.order_amount_cents
        return acc
    }, {}) : {}

    const dateArray = useMemo(() => {
        let { fromDate, toDate } = Object.assign({}, parsedFilters)

        if (!fromDate) {
            fromDate = dayjs().subtract(1, 'year')
        }
        if (!toDate) {
            toDate = dayjs()
        }
        return getDaysArray(new Date(fromDate), new Date(toDate)).map(d => dayjs(d).format('YYYY-MM-DD'))
    }, [parsedFilters])

    const chartDataArray = useMemo(() => dateArray.reduce((acc, d) => {
        if (Object.keys(acc).length === 0 && !reportDateObj[d]?.count) {
            return acc
        }
        return [...acc, { 
            date: dayjs(d).format('DD/MM/YY'), count: reportDateObj[d]?.count || 0, 
            order_amount_cents: reportDateObj[d]?.order_amount_cents / 100 || 0,
            tickets: ticketChartDateObj[d]?.count || 0,
        }]
    }, []), [dateArray, reportDateObj])


    return (
        <Layout {...{session}}>
            <div className="">
                <div className="flex space-x-3 p-3 shadow w-full overflow-x-auto">
                    <DateFilter activeKey={rawFilters.dateKey} />
                    <EventFilter token={token} filteredEvents={rawFilters.parties} />
                    <ChannelFilter token={token} filteredChannels={rawFilters.channels} />
                    <CouponFilter token={token} filteredCoupons={rawFilters.coupons} />
                    <Link href={`/report/marketing?${noSearchQueryString}`}>
                        <a>
                            <button>
                                <div className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 mr-1">
                                        <path d="M12 9a1 1 0 01-1-1V3c0-.553.45-1.008.997-.93a7.004 7.004 0 015.933 5.933c.078.547-.378.997-.93.997h-5z" />
                                        <path d="M8.003 4.07C8.55 3.992 9 4.447 9 5v5a1 1 0 001 1h5c.552 0 1.008.45.93.997A7.001 7.001 0 012 11a7.002 7.002 0 016.003-6.93z" />
                                    </svg>

                                    <p>
                                        Relatório de Marketing
                                    </p>
                                </div>
                            </button>
                        </a>
                    </Link>
                </div>
                <div className="p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Statistic 
                            title="Receita total"
                            value={money(reportSum(paymentReport?.result, 'order_amount_cents') / 100)}
                            subText={reportSum(paymentReport?.result, 'count') + ' vendas'}
                            loading={!paymentReport}
                        />
                    </div>
                    <div>
                        <Statistic
                            title="Ingressos" 
                            value={reportSum(ticketReport?.result, 'count')}
                            subText={(paymentReport?.result?.length ? (reportSum(ticketReport?.result, 'count') / reportSum(paymentReport?.result, 'count')).toFixed(1) : 0) + " ingressos/venda"}
                            loading={!ticketReport}
                        />
                    </div>
                    <div>
                        <Statistic
                            title={showIngressoMedio ? "Ingresso m\u00e9dio" : "Venda m\u00e9dia"}
                            loading={!(paymentReport && ticketReport)}
                            value={showIngressoMedio
                                ? (ticketReport?.result?.length ? money(reportSum(paymentReport?.result, 'order_amount_cents') / 100 / reportSum(ticketReport?.result, 'count')) : 0)
                                : (paymentReport?.result?.length ? money(reportSum(paymentReport?.result, 'order_amount_cents') / 100 / reportSum(paymentReport?.result, 'count')) : 0)
                            }
                            subText={
                                <span
                                    className="cursor-pointer text-primary hover:underline"
                                    onClick={() => setShowIngressoMedio(!showIngressoMedio)}
                                >
                                    {showIngressoMedio ? "Ver venda média" : "Ver ingresso médio"}
                                </span>
                            }
                        />
                    </div>
                </div>
                <div className="flex my-3 h-64 w-full">
                    <ResponsiveContainer>
                        <ComposedChart width={500} height={400} data={chartDataArray}>
                            <Bar yAxisId="right" name="Vendas" type="monotone" dataKey="count" fill="#ea560d" />
                            <Bar yAxisId="right" name="Ingressos" type="monotone" dataKey="tickets" fill="#EB7E94" />
                            <Line yAxisId="left" name="Total (R$)" type="monotone" dataKey="order_amount_cents" stroke="#A71B2E" strokeWidth={2} />
                            <CartesianGrid stroke="#ccc" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" unit="R$" />
                            <YAxis yAxisId="right" orientation="right"  />
                            <Legend />
                            <Tooltip />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
                <div className="flex"> 
                    <Tabs tabs={tabs} />
                </div>
            </div>
            </div>
        </Layout>
    )
}

